import React, { useState, useEffect } from 'react';
import {
  Box,
  CircularProgress,
  Autocomplete,
  createFilterOptions,
  TextField,
  Chip,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { SegmentedControl } from '../ui/SegmentedControl';
import { InitialsAvatar } from '../ui/InitialsAvatar';
import { ContestFormDialogLayout, FormFooterButtons, PersonOptionRow } from './ContestFormDialogLayout';
import { Team, PublicUser } from '../../models/types';
import { apiService } from '../../services/api.service';

interface TeamMemberFormProps {
  team: Team | null;
  groupId: string;
  onSave: (data: { userId: string } | { userIds: string[] }) => void;
  onCancel: () => void;
  isIndividualContest?: boolean;
  allTeams?: Team[]; // All teams in the contest to check for existing members
}

export const TeamMemberForm = ({ team, groupId, onSave, onCancel, isIndividualContest = false, allTeams = [] }: TeamMemberFormProps) => {
  const { t } = useTranslation('contests');
  const [selectedUser, setSelectedUser] = useState<PublicUser | null>(null);
  const [selectedUsers, setSelectedUsers] = useState<PublicUser[]>([]);
  const [allGroupMembers, setAllGroupMembers] = useState<PublicUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [multiSelectMode, setMultiSelectMode] = useState(false);

  // Load initial users when component mounts
  useEffect(() => {
    const loadInitialUsers = async () => {
      try {
        setLoading(true);
        
        // For both individual and team contests, only load group members
        const groupMembers = await apiService.getGroupMembers(groupId);
        setAllGroupMembers(groupMembers.members);
      } catch (error) {
        console.error('Error loading users:', error);
      } finally {
        setLoading(false);
      }
    };

    loadInitialUsers();
  }, [groupId]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Custom validation for multi-select mode
    if (multiSelectMode) {
      if (selectedUsers.length === 0) {
        return; // Don't submit if no users selected
      }
      onSave({ userIds: selectedUsers.map(user => user._id) });
    } else {
      if (!selectedUser) {
        return; // Don't submit if no user selected
      }
      onSave({ userId: selectedUser._id });
    }
  };

  const getUserDisplayName = (user: PublicUser): string => {
    if (user.stravaProfile?.firstname && user.stravaProfile?.lastname) {
      return `${user.stravaProfile.firstname} ${user.stravaProfile.lastname}`;
    }
    return user.username || user._id || t('unknownUser');
  };

  const getUserSecondaryInfo = (user: PublicUser): string => {
    const parts = [];
    if (user.username) parts.push(`@${user.username}`);
    if (user.email) parts.push(user.email);
    return parts.join(' • ');
  };

  if (!team && !isIndividualContest) {
    return null;
  }

  // Filter out users who are already team members
  let unavailableUserIds: string[] = [];
  
  if (isIndividualContest) {
    // For individual contests, no filtering needed (users can participate)
    unavailableUserIds = [];
  } else {
    // For team contests, exclude users who are already in ANY team in this contest
    unavailableUserIds = allTeams.flatMap(tm => tm.members?.map(member => member.userId) || []);
  }
  
  const availableUsers = allGroupMembers.filter(user => !unavailableUserIds.includes(user._id));

  const title = isIndividualContest ? t('participantForm.addOne') : t('memberForm.title', { team: team?.name });
  const subtitle = isIndividualContest
    ? t('memberForm.individualSubtitle')
    : team
      ? `${t('memberForm.subtitle')} · ${t('memberForm.currentMembers', { count: team.numberOfMember ?? 0 })}`
      : t('memberForm.subtitle');

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <ContestFormDialogLayout
        title={title}
        subtitle={subtitle}
        onClose={onCancel}
        footer={
          <FormFooterButtons
            onCancel={onCancel}
            submitDisabled={multiSelectMode ? selectedUsers.length === 0 : !selectedUser}
            submitLabel={
              isIndividualContest
                ? t('participantForm.addOne')
                : multiSelectMode
                  ? t('memberForm.addCount', { count: selectedUsers.length })
                  : t('memberForm.addOne')
            }
          />
        }
      >
        {!isIndividualContest && (
          <SegmentedControl
            ariaLabel={t('participantForm.selectionMode')}
            value={multiSelectMode ? 'multiple' : 'single'}
            onChange={(v) => {
              setMultiSelectMode(v === 'multiple');
              // Clear selections when switching modes
              setSelectedUser(null);
              setSelectedUsers([]);
            }}
            options={[
              { value: 'single', label: t('memberForm.oneMember') },
              { value: 'multiple', label: t('memberForm.multipleMembers') },
            ]}
          />
        )}

        <Autocomplete
          multiple={multiSelectMode}
          options={availableUsers}
          value={multiSelectMode ? selectedUsers : selectedUser}
          onChange={(event, newValue) => {
            if (multiSelectMode) {
              setSelectedUsers(newValue as PublicUser[]);
            } else {
              setSelectedUser(newValue as PublicUser | null);
            }
          }}
          inputValue={inputValue}
          onInputChange={(event, newInputValue) => setInputValue(newInputValue)}
          getOptionLabel={(option) => getUserDisplayName(option)}
          // Match on name, username and email (accent-insensitive), not just the display label
          filterOptions={createFilterOptions({
            stringify: (option: PublicUser) => [getUserDisplayName(option), option.username, option.email].filter(Boolean).join(' '),
          })}
          loading={loading}
          loadingText={t('memberForm.searching')}
          noOptionsText={
            inputValue
              ? t('memberForm.noUsers')
              : isIndividualContest
                ? t('memberForm.startTyping')
                : t('memberForm.startTypingTeam')
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={multiSelectMode ? t('memberForm.selectMany') : t('memberForm.selectOne')}
              placeholder={t('memberForm.searchPlaceholder')}
              slotProps={{
                input: {
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps?.endAdornment}
                    </>
                  ),
                }
              }}
            />
          )}
          renderOption={(props, option) => {
            const { key, ...rest } = props as typeof props & { key?: React.Key };
            return (
              <li key={option._id} {...rest}>
                <PersonOptionRow name={getUserDisplayName(option)} secondary={getUserSecondaryInfo(option)} />
              </li>
            );
          }}
          renderTags={(tagValue, getTagProps) =>
            tagValue.map((option, index) => {
              const { key, ...tagProps } = getTagProps({ index });
              return (
                <Chip
                  {...tagProps}
                  key={option._id}
                  size="small"
                  label={getUserDisplayName(option)}
                  avatar={<InitialsAvatar name={getUserDisplayName(option)} size={22} />}
                  variant="outlined"
                />
              );
            })
          }
          isOptionEqualToValue={(option, value) => option._id === value._id}
        />

        {!multiSelectMode && selectedUser && (
          <Box sx={{ p: '12px 14px', border: '1px solid var(--border)', borderRadius: '10px', backgroundColor: 'var(--muted-bg)' }}>
            <PersonOptionRow name={getUserDisplayName(selectedUser)} secondary={getUserSecondaryInfo(selectedUser)} />
          </Box>
        )}

        {multiSelectMode && selectedUsers.length > 0 && (
          <Box sx={{ fontSize: 13, color: 'var(--muted)' }}>
            {t('memberForm.selectedCount', { count: selectedUsers.length })}
          </Box>
        )}
      </ContestFormDialogLayout>
    </Box>
  );
};
